// ============================================================================
// E-Sign envelope tracker (all-employee) — /money/envelopes.
// Lists envelopes with per-recipient status; "New envelope" creates a Draft
// from a title + PDF and jumps into the prep screen.
//
// AFB-0075 / W-09 (Antonela Chesini, 2026-08-11) — "New envelope" can now start
// from a TEMPLATE that carries its own document, so a repeat send needs a title
// and nothing else. Templates already held the covering email and the field
// layout; the document was the missing piece, and it is why she said "I have to
// create a new envelope from scratch every time".
//
// A template gets its document here too, one file input per template behind a
// toggle. She already has these PDFs as files — routing that through an existing
// envelope would be a longer path to the same place, and the envelope's own PDF
// is the SEALED copy once it completes (see envelope-lib for why that matters).
//
// AFB-0075 / PR 2 (2026-08-19) — the same panel now authors the rest of what a
// packet member needs: document type, checklist item, signing ROLES (never
// emails) and per-field merge bindings. See ./template-authoring. The page count
// shown beside the file size is PARSED at upload and is read-only here on
// purpose — packet assembly offsets every later document by it.
//
// AFB-0075 / PR 3 (2026-08-19) — and now the PACKETS those templates go into,
// behind their own toggle beside "New envelope". See ./packet-manager. A packet
// is an ordered set of templates plus the rules for who gets which of them, so
// it is edited on the same surface and from the same template list; the rules
// themselves are rows, never code, and the selection arithmetic is pure and
// tested in ./packet-composition.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Ban, FileSignature, Layers, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import {
  EnvelopeRow, EsignDocumentType, EsignTemplate, FieldBindingOption, RecipientRow,
  TemplateField, TemplateRecipientRole, clearTemplateDocument,
  createEnvelopeFromTemplate, envCreate, envDelete, envVoid, fileToBase64,
  formatDocumentSize, getTemplate, listFieldBindings, listTemplates,
  recipientColor, saveLayoutToTemplate, saveTemplateDocument, saveTemplateMeta,
  saveTemplateRoles, templateHasDocument,
} from './envelope-lib';
import { TemplateAuthoring } from './template-authoring';
import { PacketManager } from './packet-manager';
import {
  DERIVED_STATUS_STYLE, daysRemaining, deriveStatus,
} from './envelope-status';
import { errorMessage } from '../../shared/errors';

const FILTERS = ['All', 'Draft', 'Sent', 'In_Progress', 'Completed', 'Voided'] as const;

const STATUS_STYLE: Record<string, string> = {
  Draft: 'bg-tcs-surface text-tcs-text-secondary',
  Sent: 'bg-st-info-bg text-st-info-fg',
  In_Progress: 'bg-st-warn-bg text-st-warn-fg-strong',
  Completed: 'bg-st-ok-bg text-st-ok-fg',
  Declined: 'bg-st-danger-bg text-st-danger-fg',
  Voided: 'bg-tcs-surface text-tcs-text-muted',
};

const RCP_DOT: Record<string, string> = {
  Pending: 'bg-tcs-border-strong',
  Notified: 'bg-st-info-solid',
  Viewed: 'bg-st-warn-solid',
  Signed: 'bg-st-ok-solid',
  Declined: 'bg-st-danger-solid',
};

interface EnvelopesData {
  envelopes: EnvelopeRow[];
  recipients: RecipientRow[];
}

// Flat-fetch + client-join (house pattern — no PostgREST embeds).
function useEnvelopes() {
  const [data, setData] = useState<EnvelopesData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: envs, error: eErr } = await supabase
        .from('esign_envelopes')
        .select('*')
        .order('created_at', { ascending: false });
      if (eErr) throw eErr;
      const ids = ((envs ?? []) as EnvelopeRow[]).map((e) => e.envelope_id);
      let recipients: RecipientRow[] = [];
      if (ids.length > 0) {
        const { data: rcps, error: rErr } = await supabase
          .from('esign_recipients')
          .select('*')
          .in('envelope_id', ids)
          .order('signing_order', { ascending: true });
        if (rErr) throw rErr;
        recipients = (rcps ?? []) as RecipientRow[];
      }
      setData({ envelopes: (envs ?? []) as EnvelopeRow[], recipients });
    } catch (e) {
      setError(errorMessage(e, 'load_failed'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, isLoading, error, reload: load };
}

export function EnvelopesPage() {
  const navigate = useNavigate();
  const { data, isLoading, error, reload } = useEnvelopes();
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // AFB-0075 / PR 3 — the packet editor, on the same surface as the templates
  // it is built from. Collapsed by default: it is set up rarely and read often.
  const [showPackets, setShowPackets] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('All');
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  // AFB-0075 — reusable templates that carry their own document.
  const [templates, setTemplates] = useState<EsignTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [showTplDocs, setShowTplDocs] = useState(false);
  const [tplBusy, setTplBusy] = useState<string | null>(null);
  const [tplMsg, setTplMsg] = useState<string | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);
  // AFB-0075 / PR 2 — template authoring: type, checklist item, roles, fields.
  const [tplEditing, setTplEditing] = useState<string | null>(null);
  const [bindings, setBindings] = useState<FieldBindingOption[]>([]);
  const [bindingsError, setBindingsError] = useState<string | null>(null);

  const chosenTemplate = useMemo(
    () => templates.find((t) => t.template_id === templateId) ?? null,
    [templates, templateId],
  );

  useEffect(() => {
    // Never throws into the page: a template list that fails to load costs a
    // shortcut, not the envelope tracker.
    listTemplates()
      .then(setTemplates)
      .catch((e) => console.error('[esign] template list failed', e));
  }, []);

  useEffect(() => {
    // The merge-binding whitelist. Loaded once and passed down: it is the only
    // source the tagging dropdown may offer, so if it fails the editor says so
    // rather than falling back to a free-text box.
    listFieldBindings()
      .then(setBindings)
      .catch((e) => setBindingsError(errorMessage(e, 'load_failed')));
  }, []);

  // Choosing a template that carries a document replaces the upload, so the
  // file input is not required. Choosing one WITHOUT a document changes nothing
  // — the title/PDF path still applies and the picker says so.
  const templateSuppliesDocument = Boolean(
    chosenTemplate && templateHasDocument(chosenTemplate),
  );

  // Search matches title, envelope id, and recipient name/email.
  // Newest first is the default order (created_at desc from the query).
  const visible = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.envelopes.filter((env) => {
      if (filter !== 'All' && env.status !== filter) return false;
      if (!q) return true;
      if (env.title.toLowerCase().includes(q)) return true;
      if (env.envelope_id.toLowerCase().includes(q)) return true;
      return data.recipients.some(
        (r) =>
          r.envelope_id === env.envelope_id &&
          (r.email.toLowerCase().includes(q) ||
            (r.name ?? '').toLowerCase().includes(q)),
      );
    });
  }, [data, query, filter]);

  async function voidEnvelope(env: EnvelopeRow) {
    if (!window.confirm(`Void "${env.title}"? Outstanding signing links stop working immediately.`)) return;
    setActionBusy(env.envelope_id);
    setActionError(null);
    try {
      await envVoid(env.envelope_id);
      await reload();
    } catch (e) {
      setActionError(errorMessage(e, 'Void failed.'));
    } finally {
      setActionBusy(null);
    }
  }

  async function deleteEnvelope(env: EnvelopeRow) {
    if (!window.confirm(`Delete "${env.title}" permanently?`)) return;
    setActionBusy(env.envelope_id);
    setActionError(null);
    try {
      await envDelete(env.envelope_id);
      await reload();
    } catch (e) {
      setActionError(errorMessage(e, 'Delete failed.'));
    } finally {
      setActionBusy(null);
    }
  }

  // AFB-0075 (Antonela): "I already have my common documents ready to go, I just
  // update the information I need and send them. With this feature, I have to
  // create a new envelope from scratch every time."
  //
  // Templates already carried the covering email and the field layout; what they
  // had no column for was the DOCUMENT, so every send still started at a file
  // picker. Picking a template that carries one replaces the upload step
  // entirely; the file input stays for everything else.
  async function createEnvelope() {
    setCreating(true);
    setCreateError(null);
    try {
      if (chosenTemplate) {
        // A template with no saved document still contributes its covering
        // email, so that case goes through the same call with the sender's file.
        const env = await createEnvelopeFromTemplate({
          template: chosenTemplate,
          title: title.trim(),
          pdfBase64Override:
            !templateSuppliesDocument && file ? await fileToBase64(file) : undefined,
        });
        navigate(`/money/envelopes/${env.envelope_id}`);
        return;
      }
      const pdf_base64 = file ? await fileToBase64(file) : undefined;
      const res = await envCreate({ title: title.trim(), pdf_base64 });
      navigate(`/money/envelopes/${res.envelope.envelope_id}`);
    } catch (e) {
      setCreateError(errorMessage(e, 'Create failed.'));
      setCreating(false);
    }
  }

  /**
   * Run one template write and re-read the row it touched.
   *
   * Every template edit on this panel has the same shape — busy, write, re-read,
   * say what happened — and the re-read matters more than it looks: page_count
   * is written by a second RPC inside saveTemplateDocument, so the row in state
   * is the only place the two can be seen agreeing.
   */
  async function editTemplate(
    template_id: string,
    write: () => Promise<void>,
    done: string,
    failed: string,
  ) {
    setTplBusy(template_id);
    setTplError(null);
    setTplMsg(null);
    try {
      await write();
      const fresh = await getTemplate(template_id);
      if (fresh) {
        setTemplates((cur) => cur.map((t) => (t.template_id === template_id ? fresh : t)));
      }
      setTplMsg(done);
    } catch (e) {
      setTplError(errorMessage(e, failed));
    } finally {
      setTplBusy(null);
    }
  }

  /** Attach / replace the reusable PDF on a template. */
  async function attachTemplateDocument(templateId: string, f: File) {
    await editTemplate(
      templateId,
      () => saveTemplateDocument(templateId, f),
      `Saved ${f.name} to the template. New envelopes can start from it.`,
      'Could not save the document to the template.',
    );
  }

  async function saveMeta(
    t: EsignTemplate,
    meta: { document_type: EsignDocumentType; checklist_key: string | null },
  ) {
    await editTemplate(
      t.template_id,
      () => saveTemplateMeta(t.template_id, meta),
      `Saved the details for "${t.name}".`,
      'Could not save the template details.',
    );
  }

  async function saveRoles(t: EsignTemplate, roles: TemplateRecipientRole[]) {
    await editTemplate(
      t.template_id,
      () => saveTemplateRoles(t.template_id, roles),
      roles.length === 0
        ? `Cleared the roles on "${t.name}".`
        : `Saved ${roles.length} role${roles.length === 1 ? '' : 's'} on "${t.name}". ` +
          `No email addresses were stored — they are matched at send time.`,
      'Could not save the roles.',
    );
  }

  async function saveTagging(t: EsignTemplate, layout: TemplateField[]) {
    await editTemplate(
      t.template_id,
      () => saveLayoutToTemplate(t.template_id, layout),
      `Saved the field tagging on "${t.name}".`,
      'Could not save the field tagging.',
    );
  }

  async function removeTemplateDocument(t: EsignTemplate) {
    if (
      !window.confirm(
        `Remove "${t.document_filename ?? 'the document'}" from "${t.name}"? ` +
          `Envelopes already created from it are unaffected.`,
      )
    ) {
      return;
    }
    setTplBusy(t.template_id);
    setTplError(null);
    setTplMsg(null);
    try {
      await clearTemplateDocument(t.template_id);
      const fresh = await getTemplate(t.template_id);
      if (fresh) {
        setTemplates((cur) => cur.map((x) => (x.template_id === t.template_id ? fresh : x)));
      }
      if (templateId === t.template_id) setTemplateId('');
      setTplMsg(`Removed the document from "${t.name}".`);
    } catch (e) {
      setTplError(errorMessage(e, 'Could not remove the document.'));
    } finally {
      setTplBusy(null);
    }
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-tcs-text">
            <FileSignature className="h-5 w-5" aria-hidden /> E-Sign envelopes
          </h2>
          <p className="text-xs text-tcs-text-muted">
            Multi-signer documents with placed fields and sequential routing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* AFB-0075 / PR 3 — packets are set up here rather than behind their
              own route: a packet is made of templates and is edited in the same
              sitting as they are. Its own toggle, not tucked inside "New
              envelope", because it is not part of sending one. */}
          <button
            type="button"
            onClick={() => setShowPackets((v) => !v)}
            aria-expanded={showPackets}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm text-tcs-text-secondary"
          >
            <Layers className="h-4 w-4" aria-hidden /> Packets
          </button>
          <button
            type="button"
            onClick={() => setShowNew((v) => !v)}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-md bg-tcs-accent-ink px-4 py-2 text-sm font-semibold text-tcs-accent-on"
          >
            <Plus className="h-4 w-4" aria-hidden /> New envelope
          </button>
        </div>
      </div>

      {showPackets && (
        <div className="mb-4 rounded-md border border-tcs-border bg-tcs-raised p-4">
          <PacketManager templates={templates} />
        </div>
      )}

      {showNew && (
        <div className="mb-4 rounded-md border border-tcs-border bg-tcs-raised p-4">
          <label htmlFor="env-title" className="block text-xs font-medium text-tcs-text-secondary">
            Title *
          </label>
          <input
            id="env-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Project Proposal — KC Cardiology strip & wax"
            className="mb-2 mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
          />
          {/* AFB-0075 — start from a template. Listed only when at least one
              exists; each option says whether it brings its own document, so
              nobody picks one expecting a PDF that isn't there. */}
          {templates.length > 0 && (
            <>
              <label
                htmlFor="env-template"
                className="block text-xs font-medium text-tcs-text-secondary"
              >
                Start from a template
              </label>
              <select
                id="env-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="mb-2 mt-1 w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
              >
                <option value="">No template — upload a PDF below</option>
                {templates.map((t) => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.name}
                    {templateHasDocument(t)
                      ? ` — ${t.document_filename ?? 'document'} (${formatDocumentSize(t.document_bytes)})`
                      : ' — email text only, no document'}
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="env-pdf" className="block text-xs font-medium text-tcs-text-secondary">
            Document PDF {templateSuppliesDocument ? '(supplied by the template)' : '*'}
          </label>
          {templateSuppliesDocument ? (
            <p className="mb-3 mt-1 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
              Using <strong>{chosenTemplate?.document_filename ?? 'the saved document'}</strong>{' '}
              ({formatDocumentSize(chosenTemplate?.document_bytes)}) from “{chosenTemplate?.name}”.
              The covering email and note to the signer come with it; field placements are applied
              on the next screen, once you've added the recipients.
            </p>
          ) : (
            <input
              id="env-pdf"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mb-3 mt-1 w-full text-xs text-tcs-text-secondary"
            />
          )}
          <button
            type="button"
            disabled={
              creating || title.trim().length < 3 || (!file && !templateSuppliesDocument)
            }
            onClick={createEnvelope}
            className="rounded-md bg-tcs-accent-ink px-4 py-2.5 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
          >
            {creating ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Creating…
              </span>
            ) : 'Create & place fields'}
          </button>
          {createError && (
            <p role="alert" className="mt-2 text-xs text-st-danger-fg">{createError}</p>
          )}

          {/* AFB-0075 — where a template GETS its document. Antonela already has
              her common documents as files, so the shortest path is a file input
              per template rather than a round trip through an envelope. Tucked
              behind a toggle: it is set up once and then never looked at again. */}
          {templates.length > 0 && (
            <div className="mt-4 border-t border-tcs-border pt-3">
              <button
                type="button"
                onClick={() => setShowTplDocs((v) => !v)}
                className="text-xs font-medium text-tcs-text-secondary underline"
              >
                {showTplDocs ? 'Hide' : 'Manage'} template documents
              </button>
              {showTplDocs && (
                <ul className="mt-2 space-y-2">
                  {templates.map((t) => (
                    <li
                      key={t.template_id}
                      className="rounded-md border border-tcs-border bg-tcs-bg-muted p-2 text-xs"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-tcs-text">{t.name}</span>
                        {templateHasDocument(t) ? (
                          <span className="text-tcs-text-secondary">
                            {t.document_filename ?? 'document'} ·{' '}
                            {formatDocumentSize(t.document_bytes)} ·{' '}
                            {/* Parsed from the PDF, never typed. A blank here is
                                the tell that this template cannot join a packet. */}
                            {t.page_count && t.page_count > 0 ? (
                              `${t.page_count} page${t.page_count === 1 ? '' : 's'}`
                            ) : (
                              <span className="text-st-warn-fg">page count unknown</span>
                            )}
                          </span>
                        ) : (
                          <span className="italic text-tcs-text-muted">no document saved</span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <input
                          type="file"
                          accept="application/pdf"
                          aria-label={`Document for ${t.name}`}
                          disabled={tplBusy === t.template_id}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            // Reset the input so re-picking the same file fires
                            // onChange again — otherwise a failed save cannot be
                            // retried without choosing a different file.
                            e.target.value = '';
                            if (f) void attachTemplateDocument(t.template_id, f);
                          }}
                          className="text-xs text-tcs-text-secondary"
                        />
                        {templateHasDocument(t) && (
                          <button
                            type="button"
                            disabled={tplBusy === t.template_id}
                            onClick={() => void removeTemplateDocument(t)}
                            className="rounded border border-tcs-border px-2 py-1 text-[11px] text-tcs-text-secondary hover:border-rag-red hover:text-rag-red disabled:opacity-50"
                          >
                            Remove
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setTplEditing(
                            (cur) => (cur === t.template_id ? null : t.template_id),
                          )}
                          aria-expanded={tplEditing === t.template_id}
                          className="rounded border border-tcs-border px-2 py-1 text-[11px] text-tcs-text-secondary"
                        >
                          {tplEditing === t.template_id ? 'Done editing' : 'Edit template'}
                        </button>
                        {tplBusy === t.template_id && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-tcs-text-muted" aria-hidden />
                        )}
                      </div>
                      {tplEditing === t.template_id && (
                        <TemplateAuthoring
                          template={t}
                          bindings={bindings}
                          bindingsError={bindingsError}
                          busy={tplBusy === t.template_id}
                          onSaveMeta={(meta) => saveMeta(t, meta)}
                          onSaveRoles={(roles) => saveRoles(t, roles)}
                          onSaveLayout={(layout) => saveTagging(t, layout)}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {tplMsg && <p className="mt-2 text-xs text-st-ok-fg">{tplMsg}</p>}
              {tplError && (
                <p role="alert" className="mt-2 text-xs text-st-danger-fg">{tplError}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* search + status filter (newest first by default) */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-tcs-text-muted" aria-hidden />
          <input
            aria-label="Search envelopes"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search title, ID, or recipient…"
            className="w-full rounded-md border border-tcs-border py-2 pl-8 pr-3 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full border px-2.5 py-1 text-xs ${
                filter === f
                  ? 'border-tcs-accent-ink bg-tcs-accent-ink text-tcs-accent-on'
                  : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary'
              }`}
            >
              {f.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {actionError && (
        <p role="alert" className="mb-3 text-sm text-st-danger-fg">{actionError}</p>
      )}

      {isLoading && (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-tcs-text-muted" aria-label="Loading" />
        </div>
      )}
      {error != null && (
        <p className="text-sm text-st-danger-fg">Could not load envelopes.</p>
      )}

      {data && data.envelopes.length === 0 && !showNew && (
        <p className="rounded-md border border-dashed border-tcs-border p-6 text-center text-sm text-tcs-text-muted">
          No envelopes yet. Create one to send a document for multi-party signature.
        </p>
      )}
      {data && data.envelopes.length > 0 && visible.length === 0 && (
        <p className="rounded-md border border-dashed border-tcs-border p-6 text-center text-sm text-tcs-text-muted">
          Nothing matches this search/filter.
        </p>
      )}

      <ul className="space-y-2">
        {visible.map((env) => {
          const rcps = (data?.recipients ?? []).filter(
            (r) => r.envelope_id === env.envelope_id,
          );
          const derived = deriveStatus(env, rcps);
          const left = daysRemaining(env);
          const canVoid = ['Sent', 'In_Progress'].includes(env.status);
          const canDelete = ['Draft', 'Voided'].includes(env.status);
          return (
            <li key={env.envelope_id}>
              <Link
                to={`/money/envelopes/${env.envelope_id}`}
                className="block rounded-md border border-tcs-border bg-tcs-raised p-3 hover:bg-tcs-bg-muted"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-semibold text-tcs-text">
                    {env.title}
                  </p>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {canVoid && (
                      <button
                        type="button"
                        title="Void — outstanding links stop working"
                        disabled={actionBusy === env.envelope_id}
                        onClick={(e) => {
                          e.preventDefault();
                          void voidEnvelope(env);
                        }}
                        className="rounded border border-tcs-border p-1.5 text-tcs-text-muted hover:text-st-warn-fg disabled:opacity-50"
                      >
                        <Ban className="h-3.5 w-3.5" aria-label="Void envelope" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        title="Delete permanently"
                        disabled={actionBusy === env.envelope_id}
                        onClick={(e) => {
                          e.preventDefault();
                          void deleteEnvelope(env);
                        }}
                        className="rounded border border-tcs-border p-1.5 text-tcs-text-muted hover:text-st-danger-fg disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-label="Delete envelope" />
                      </button>
                    )}
                <span
                      className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                        DERIVED_STATUS_STYLE[derived] ?? STATUS_STYLE[env.status] ?? ''
                      }`}
                    >
                      {derived}
                    </span>
                  </span>
                </div>
                <p className="mt-0.5 text-[11px] text-tcs-text-muted">
                  {env.envelope_id} · created {new Date(env.created_at).toLocaleDateString()}
                  {/* Only surface the countdown when it is actionable: an
                      envelope quietly running out of time is the thing a
                      sender most needs to be told about. */}
                  {left != null && left > 0 && left <= 3 && (
                    <span className="ml-1 font-medium text-st-warn-fg">
                      · {left} day{left === 1 ? '' : 's'} left to sign
                    </span>
                  )}
                  {derived === 'Expired' && (
                    <span className="ml-1 font-medium text-st-warn-fg-strong">
                      · link expired, resend to restart
                    </span>
                  )}
                </p>
                {rcps.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {rcps.map((r, i) => (
                      <li
                        key={r.recipient_id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-tcs-border px-2 py-0.5 text-[11px] text-tcs-text-secondary"
                        style={{ borderColor: recipientColor(i) }}
                      >
                        <span
                          className={`h-2 w-2 rounded-full ${RCP_DOT[r.status] ?? 'bg-tcs-border-strong'}`}
                          aria-hidden
                        />
                        {r.name ?? r.email}
                        <span className="text-tcs-text-muted">
                          · {r.role === 'CC' ? 'CC' : `#${r.signing_order}`} {r.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
